// path: frontend/src/components/pages/chat/usage/Usage.jsx
import React, { useState, useEffect, useCallback, useRef } from "react";
import { ChevronDown, TrendingUp, Receipt, Loader2 } from "lucide-react";
import {
  getUserUsageAndPricing,
  getUserFirstMonthStatus,
} from "../../../../api/apiFunction/userServices";

const Usage = ({ userId }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [activeTab, setActiveTab] = useState("usage");
  const [usageData, setUsageData] = useState(null);
  const [firstMonthStatus, setFirstMonthStatus] = useState({
    firstMonth: false,
    credit: 0,
    base_cost: 0,
  });
  const [loading, setLoading] = useState(true);
  const [firstMonthLoading, setFirstMonthLoading] = useState(false);
  const [usagePeriod, setUsagePeriod] = useState("today"); // "today" or "monthly"
  const [showPeriodDropdown, setShowPeriodDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const periodDropdownRef = useRef(null);

  const fetchUsageData = useCallback(async () => {
    if (!userId) return;
    try {
      setLoading(true);
      const response = await getUserUsageAndPricing(userId);
      if (response?.data?.success) {
        setUsageData(response.data);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const fetchFirstMonthStatus = useCallback(async () => {
    if (!userId) return;
    try {
      setFirstMonthLoading(true);
      const res = await getUserFirstMonthStatus(userId);
      const data = res?.data || {};
      setFirstMonthStatus({
        firstMonth: !!data.firstMonth,
        credit: Number(data.credit) || 0,
        base_cost: Number(data.base_cost) || 0,
      });
    } catch (error) {
    } finally {
      setFirstMonthLoading(false);
    }
  }, [userId]);

  // Initial fetch and refresh on user change
  useEffect(() => {
    fetchUsageData();
    fetchFirstMonthStatus();
  }, [fetchUsageData, fetchFirstMonthStatus]);

  // Listen for global refresh events after chat responses
  useEffect(() => {
    const handler = () => {
      fetchUsageData();
      fetchFirstMonthStatus();
    };
    window.addEventListener("usage:refresh", handler);
    return () => window.removeEventListener("usage:refresh", handler);
  }, [fetchUsageData, fetchFirstMonthStatus]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
      if (
        periodDropdownRef.current &&
        !periodDropdownRef.current.contains(event.target)
      ) {
        setShowPeriodDropdown(false);
      }
    };

    if (showDropdown || showPeriodDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDropdown, showPeriodDropdown]);

  const usagePercentage = usageData?.pricingSnapshot?.usagePercentage || 0;

  const getUsageColor = (percentage) => {
    if (percentage >= 90) return "text-red-500";
    if (percentage >= 75) return "text-orange-500";
    if (percentage >= 50) return "text-yellow-500";
    return "text-green-500";
  };

  const getProgressBarColor = (percentage) => {
    if (percentage >= 90) return "bg-red-500";
    if (percentage >= 75) return "bg-orange-500";
    if (percentage >= 50) return "bg-yellow-500";
    return "bg-green-500";
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num?.toString() || "0";
  };

  const derivePercentage = (used = 0, limit = -1, pct) => {
    if (typeof pct === "number") return pct;
    if (!limit || limit <= 0 || limit === -1) return 0;
    return Math.min((used / limit) * 100, 100);
  };

  const UsageMeterIcon = ({ width = 16, height = 16, className = "" }) => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      viewBox="0 0 256 256"
      className={className}
    >
      <g fill="currentColor">
        <path d="m96 40l33.52 88H56Zm104 88h-70.48L160 208Z" opacity=".2" />
        <path d="M240 128a8 8 0 0 1-8 8h-27.06l-37.78 75.58A8 8 0 0 1 160 216h-.4a8 8 0  0 1-7.08-5.14L95.35 60.76l-32.07 70.55A8 8 0 0 1 56 136H24a8 8 0 0 1 0-16h26.85l37.87-83.31a8 8 0 0 1 14.76.46l57.51 151l31.85-63.71A8 8 0 0 1 200 120h32a8 8 0 0 1 8 8" />
      </g>
    </svg>
  );

  const renderUsageTab = () => {
    if (loading) {
      return (
        <div className="p-4 flex flex-col items-center justify-center py-8">
          <Loader2 size={24} className="animate-spin text-fg-60 mb-2" />
          <span className="text-sm text-fg-60">Loading usage data...</span>
        </div>
      );
    }

    if (!usageData) {
      return (
        <div className="p-4 flex flex-col items-center justify-center py-8">
          <span className="text-sm text-fg-60 mb-1">
            No usage data available
          </span>
          <span className="text-xs text-fg-70">
            Please check your connection
          </span>
        </div>
      );
    }

    // Get data based on selected period
    const isToday = usagePeriod === "today";
    const usageSource = isToday ? usageData.dailyUsage : usageData.monthlyUsage;

    // DEBUG LOGS FOR IMAGE / NANO BANANA USAGE
    try {
      if (usageSource && typeof usageSource === "object") {
        // Log any keys that look image-related
        const imageLike = Object.entries(usageSource).filter(([key]) =>
          /image|banana/i.test(key)
        );
      }
    } catch (e) {
      // Never block UI on logging
    }

    const highEndTokensUsed = usageSource?.highEndLlmTokens?.used || 0;
    const smallTokensUsed = usageSource?.smallLlmTokens?.used || 0;

    // --- IMAGE USAGE (robust to different backend field names) ---
    const baseImages = usageSource?.images || {};
    let imagesUsed = baseImages.used || 0;
    let imagesLimit = baseImages.limit ?? -1;
    let imagesPctOverride = baseImages.percentage;

    // Fallback: if imagesUsed is 0 but backend uses other image keys
    // (e.g. imageGenerations, nanoBananaImages, etc.), aggregate them.
    if (!imagesUsed && usageSource && typeof usageSource === "object") {
      const imageEntries = Object.entries(usageSource).filter(
        ([key, value]) =>
          key !== "images" &&
          /image/i.test(key) &&
          value &&
          typeof value === "object" &&
          typeof value.used === "number"
      );

      if (imageEntries.length > 0) {
        imagesUsed = imageEntries.reduce(
          (sum, [, value]) => sum + (value.used || 0),
          0
        );
        // Take limit/percentage from the first matching entry as a heuristic
        const firstImage = imageEntries[0][1];
        imagesLimit =
          typeof firstImage.limit === "number" ? firstImage.limit : imagesLimit;
        imagesPctOverride =
          typeof firstImage.percentage === "number"
            ? firstImage.percentage
            : imagesPctOverride;
      }
    }

    // Nano Banana (Gemini 2.5 Flash Image - production model) image generation usage
    // Try explicit fields first, then any key that contains "banana"
    let nanoBananaImagesUsed =
      usageSource?.nanoBananaImages?.used ??
      usageSource?.bananaImages?.used ??
      0;
    let nanoBananaImagesLimit =
      usageSource?.nanoBananaImages?.limit ??
      usageSource?.bananaImages?.limit ??
      -1;
    let nanoBananaPctOverride =
      usageSource?.nanoBananaImages?.percentage ??
      usageSource?.bananaImages?.percentage;

    if (
      !nanoBananaImagesUsed &&
      usageSource &&
      typeof usageSource === "object"
    ) {
      const bananaEntry = Object.entries(usageSource).find(
        ([key, value]) =>
          /banana/i.test(key) &&
          value &&
          typeof value === "object" &&
          typeof value.used === "number"
      );
      if (bananaEntry) {
        const bananaStats = bananaEntry[1];
        nanoBananaImagesUsed = bananaStats.used || 0;
        nanoBananaImagesLimit =
          typeof bananaStats.limit === "number"
            ? bananaStats.limit
            : nanoBananaImagesLimit;
        nanoBananaPctOverride =
          typeof bananaStats.percentage === "number"
            ? bananaStats.percentage
            : nanoBananaPctOverride;
      }
    }

    const searchesUsed = usageSource?.webSearches?.used || 0;
    const youtubeUsed = usageSource?.youtube?.used || 0;

    const highEndLimit = usageSource?.highEndLlmTokens?.limit ?? -1;
    const smallLimit = usageSource?.smallLlmTokens?.limit ?? -1;
    const searchesLimit = usageSource?.webSearches?.limit ?? -1;
    const youtubeLimit = usageSource?.youtube?.limit ?? -1;

    const highEndTokensPercentage = derivePercentage(
      highEndTokensUsed,
      highEndLimit,
      usageSource?.highEndLlmTokens?.percentage
    );
    const smallTokensPercentage = derivePercentage(
      smallTokensUsed,
      smallLimit,
      usageSource?.smallLlmTokens?.percentage
    );
    const imagesPercentage = derivePercentage(
      imagesUsed,
      imagesLimit,
      imagesPctOverride
    );
    const nanoBananaImagesPercentage = derivePercentage(
      nanoBananaImagesUsed,
      nanoBananaImagesLimit,
      nanoBananaPctOverride
    );
    const searchesPercentage = derivePercentage(
      searchesUsed,
      searchesLimit,
      usageSource?.webSearches?.percentage
    );
    const youtubePercentage = derivePercentage(
      youtubeUsed,
      youtubeLimit,
      usageSource?.youtube?.percentage
    );

    const currentTier =
      usageData.pricingSnapshot?.tier
        ?.replace("_", " ")
        .replace(/\b\w/g, (l) => l.toUpperCase()) || "Light user";

    // Use billing values directly from backend (already calculated from DB)
    // Backend now applies first-month credit automatically, so usageCost is already discounted
    const monthlyBill = Number(
      usageData.usageSummary?.billing?.finalBill ||
        usageData?.plan?.baseCost ||
        7.99
    );
    const usageCost = Number(usageData.usageSummary?.billing?.usageCost || 0); // Already discounted if first month
    const rawUsageCost = Number(
      usageData.usageSummary?.billing?.rawUsageCost || usageCost
    ); // Original usage cost before credit
    const creditApplied = Number(
      usageData.usageSummary?.billing?.creditApplied || 0
    ); // Credit amount applied
    const baseCost = Number(usageData?.plan?.baseCost || 7.99);
    const isFirstMonth = !!firstMonthStatus.firstMonth;
    const discountCredit =
      creditApplied ||
      (isFirstMonth ? Number(firstMonthStatus.credit || 0) : 0);
    const discountedUsageCost = usageCost; // Already discounted by backend

    const valueRatio = usageData.usageSummary?.valueMetrics?.valueRatio || 0;
    const marketValue = usageData.usageSummary?.valueMetrics?.marketValue || 0;

    return (
      <div className="p-3 md:p-4 space-y-3 md:space-y-4">
        {/* Usage Overview Header */}
        <div className="flex items-center justify-between">
          <h3 className="text-base md:text-lg font-semibold text-fg-50">
            Usage
          </h3>
        </div>

        {/* Usage Section */}
        <div className="bg-bg-50 rounded-lg p-2.5 md:p-3 space-y-2.5 md:space-y-3">
          <div className="flex items-center justify-between text-xs flex-wrap gap-2">
            <span className="text-sm text-fg-50 font-medium">Usage</span>
            <div className="relative" ref={periodDropdownRef}>
              <button
                onClick={() => setShowPeriodDropdown(!showPeriodDropdown)}
                className="flex items-center gap-1 px-2 py-1 bg-bg-40 rounded text-xs hover:bg-bg-30 transition-colors"
              >
                <span className="text-fg-60 capitalize">{usagePeriod}</span>
                <ChevronDown
                  size={12}
                  className={`text-fg-60 transition-transform duration-200 ${
                    showPeriodDropdown ? "rotate-180" : ""
                  }`}
                />
              </button>
              {showPeriodDropdown && (
                <div className="absolute top-full right-0 mt-1 bg-bg-60 rounded-lg shadow-lg border border-bd-50 z-50 min-w-[80px]">
                  <button
                    onClick={() => {
                      setUsagePeriod("today");
                      setShowPeriodDropdown(false);
                    }}
                    className={`w-full px-3 py-2 text-xs text-left hover:bg-bg-50 transition-colors ${
                      usagePeriod === "today"
                        ? "text-fg-50 font-medium"
                        : "text-fg-60"
                    }`}
                  >
                    Today
                  </button>
                  <button
                    onClick={() => {
                      setUsagePeriod("monthly");
                      setShowPeriodDropdown(false);
                    }}
                    className={`w-full px-3 py-2 text-xs text-left hover:bg-bg-50 transition-colors ${
                      usagePeriod === "monthly"
                        ? "text-fg-50 font-medium"
                        : "text-fg-60"
                    }`}
                  >
                    Monthly
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* High-End Tokens - Single Row Layout */}
          <div className="flex items-center gap-2 md:gap-3 flex-wrap">
            <span className="text-xs text-fg-60 w-24 md:w-28 flex-shrink-0">
              High-End Tokens
            </span>
            <div className="flex-1 bg-bg-40 rounded-full h-1.5 min-w-[60px]">
              <div
                className="bg-red-500 h-1.5 rounded-full transition-all duration-300"
                style={{ width: `${Math.min(highEndTokensPercentage, 100)}%` }}
              />
            </div>
            <span className="text-xs text-fg-50 font-medium w-10 md:w-8 text-right flex-shrink-0">
              {formatNumber(highEndTokensUsed)}
            </span>
          </div>

          {/* Low-End Tokens - Single Row Layout */}
          <div className="flex items-center gap-2 md:gap-3 flex-wrap">
            <span className="text-xs text-fg-60 w-24 md:w-28 flex-shrink-0">
              Low-End Tokens
            </span>
            <div className="flex-1 bg-bg-40 rounded-full h-1.5 min-w-[60px]">
              <div
                className="bg-orange-400 h-1.5 rounded-full transition-all duration-300"
                style={{ width: `${Math.min(smallTokensPercentage, 100)}%` }}
              />
            </div>
            <span className="text-xs text-fg-50 font-medium w-10 md:w-8 text-right flex-shrink-0">
              {formatNumber(smallTokensUsed)}
            </span>
          </div>

          {/* Images - Single Row Layout */}
          <div className="flex items-center gap-2 md:gap-3 flex-wrap">
            <span className="text-xs text-fg-60 w-24 md:w-28 flex-shrink-0">
              Images
            </span>
            <div className="flex-1 bg-bg-40 rounded-full h-1.5 min-w-[60px]">
              <div
                className="bg-blue-500 h-1.5 rounded-full transition-all duration-300"
                style={{ width: `${Math.min(imagesPercentage, 100)}%` }}
              />
            </div>
            <span className="text-xs text-fg-50 font-medium w-10 md:w-8 text-right flex-shrink-0">
              {formatNumber(imagesUsed)}
            </span>
          </div>

          {/* Nano Banana Images - Single Row Layout */}
          {(usageSource?.nanoBananaImages ||
            usageSource?.bananaImages ||
            nanoBananaImagesUsed > 0) && (
            <div className="flex items-center gap-2 md:gap-3 flex-wrap">
              <span className="text-xs text-fg-60 w-24 md:w-28 flex-shrink-0">
                Nano Banana Img
              </span>
              <div className="flex-1 bg-bg-40 rounded-full h-1.5 min-w-[60px]">
                <div
                  className="bg-yellow-500 h-1.5 rounded-full transition-all duration-300"
                  style={{
                    width: `${Math.min(nanoBananaImagesPercentage, 100)}%`,
                  }}
                />
              </div>
              <span className="text-xs text-fg-50 font-medium w-10 md:w-8 text-right flex-shrink-0">
                {formatNumber(nanoBananaImagesUsed)}
              </span>
            </div>
          )}

          {/* Web Searches - Single Row Layout */}
          <div className="flex items-center gap-2 md:gap-3 flex-wrap">
            <span className="text-xs text-fg-60 w-24 md:w-28 flex-shrink-0">
              Web Searches
            </span>
            <div className="flex-1 bg-bg-40 rounded-full h-1.5 min-w-[60px]">
              <div
                className="bg-green-500 h-1.5 rounded-full transition-all duration-300"
                style={{ width: `${Math.min(searchesPercentage, 100)}%` }}
              />
            </div>
            <span className="text-xs text-fg-50 font-medium w-10 md:w-8 text-right flex-shrink-0">
              {formatNumber(searchesUsed)}
            </span>
          </div>

          {/* YouTube - Single Row Layout */}
          <div className="flex items-center gap-2 md:gap-3 flex-wrap">
            <span className="text-xs text-fg-60 w-24 md:w-28 flex-shrink-0">
              YouTube
            </span>
            <div className="flex-1 bg-bg-40 rounded-full h-1.5 min-w-[60px]">
              <div
                className="bg-purple-500 h-1.5 rounded-full transition-all duration-300"
                style={{ width: `${Math.min(youtubePercentage, 100)}%` }}
              />
            </div>
            <span className="text-xs text-fg-50 font-medium w-10 md:w-8 text-right flex-shrink-0">
              {formatNumber(youtubeUsed)}
            </span>
          </div>

          {/* Reset Time */}
          <div className="text-[10px] text-fg-60 pt-1">
            Resets: 12:00 AM UTC
          </div>
        </div>

        {/* User Status Container */}
        <div className="bg-bg-50 rounded-lg p-2.5 md:p-3 space-y-2.5 md:space-y-3">
          {/* User Tier and Active Badge */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-fg-50 font-medium">
              {currentTier}
            </span>
            <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-xs rounded-full">
              Active
            </span>
          </div>

          {/* Usage Percentage */}
          <div>
            <div className="text-xl md:text-2xl font-bold text-fg-50 mb-1">
              {usagePercentage.toFixed(2)}%
            </div>
            <div className="text-[10px] text-fg-60">monthly usage consumed</div>
          </div>

          {/* Metrics - Left aligned labels, right aligned values */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs text-fg-60">Monthly Bill</span>
              <span className="text-xs font-medium text-fg-50">
                {formatCurrency(monthlyBill)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-fg-60">Usage Cost</span>
              <div className="text-xs font-medium text-fg-50">
                {isFirstMonth &&
                discountCredit > 0 &&
                rawUsageCost > usageCost ? (
                  <div className="flex items-center gap-1">
                    <span className="line-through text-fg-60 text-xs">
                      {formatCurrency(rawUsageCost)}
                    </span>
                    <span>{formatCurrency(discountedUsageCost)}</span>
                  </div>
                ) : (
                  formatCurrency(usageCost)
                )}
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-fg-60">Value Ratio</span>
              <span className="text-xs font-medium text-fg-50">
                {valueRatio}x
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-fg-60">Market Value</span>
              <span className="text-xs font-medium text-fg-50">
                {formatCurrency(marketValue)}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderBillingTab = () => {
    if (loading) {
      return (
        <div className="p-4 flex flex-col items-center justify-center py-8">
          <Loader2 size={24} className="animate-spin text-fg-60 mb-2" />
          <span className="text-sm text-fg-60">Loading billing data...</span>
        </div>
      );
    }

    if (!usageData) {
      return (
        <div className="p-4 flex flex-col items-center justify-center py-8">
          <span className="text-sm text-fg-60 mb-1">
            No billing data available
          </span>
          <span className="text-xs text-fg-70">
            Please check your connection
          </span>
        </div>
      );
    }

    // Use billing values directly from backend (already calculated from DB)
    // Backend now applies first-month credit automatically, so usageCost is already discounted
    const baseCost = Number(usageData?.plan?.baseCost || 7.99);
    const usageCost = Number(usageData.usageSummary?.billing?.usageCost || 0); // Already discounted if first month
    const rawUsageCost = Number(
      usageData.usageSummary?.billing?.rawUsageCost || usageCost
    ); // Original usage cost before credit
    const creditApplied = Number(
      usageData.usageSummary?.billing?.creditApplied || 0
    ); // Credit amount applied
    const totalCost = Number(
      usageData.usageSummary?.billing?.finalBill || baseCost + usageCost
    ); // Final bill includes credit
    const isFirstMonth = !!firstMonthStatus.firstMonth;
    const discountCredit =
      creditApplied ||
      (isFirstMonth ? Number(firstMonthStatus.credit || 0) : 0);
    const discountedUsageCost = usageCost; // Already discounted by backend

    const currentMonth = usageData?.monthlyUsage?.lastResetMonth;
    const monthDisplay = currentMonth
      ? new Date(currentMonth + "-01").toLocaleDateString("en-US", {
          month: "long",
          year: "numeric",
        })
      : new Date().toLocaleDateString("en-US", {
          month: "long",
          year: "numeric",
        });

    const formatBillingDate = (dateString) => {
      if (!dateString) return null;
      try {
        return new Date(dateString).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        });
      } catch {
        return null;
      }
    };

    const lastResetDate = formatBillingDate(
      usageData?.dailyUsage?.lastResetDate
    );
    const nextResetDate = currentMonth
      ? new Date(
          new Date(currentMonth + "-01").getFullYear(),
          new Date(currentMonth + "-01").getMonth() + 1,
          1
        ).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })
      : null;

    const valueRatio = usageData.usageSummary?.valueMetrics?.valueRatio || 0;
    const marketValue = usageData.usageSummary?.valueMetrics?.marketValue || 0;
    const savings = marketValue - totalCost;

    return (
      <div className="p-3 md:p-4 space-y-3 md:space-y-4">
        {/* Billing Header */}
        <div className="flex items-center justify-between">
          <h3 className="text-base md:text-lg font-semibold text-fg-50">
            Billing
          </h3>
        </div>

        {/* Current Bill Container - Full Width */}
        <div className="bg-bg-50 rounded-lg p-2.5 md:p-3 space-y-2.5 md:space-y-3">
          {/* Current Bill Header with Active Badge */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-fg-50 font-medium">Current Bill</span>
            <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-xs rounded-full">
              Active
            </span>
          </div>

          {/* Bill Amount */}
          <div className="text-left py-2">
            <div className="text-xl md:text-2xl font-bold text-fg-50">
              {formatCurrency(totalCost)}
            </div>
            <div className="text-[10px] text-fg-60 mt-1 break-words">
              for {monthDisplay}
            </div>
          </div>

          {/* Billing Breakdown - Left aligned labels, right aligned values */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs text-fg-60">
                Your fixed monthly plan charge
              </span>
              <span className="text-xs font-medium text-fg-50">
                {formatCurrency(baseCost)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-fg-60">
                Extra charges based on your usage
              </span>
              <div className="text-xs font-medium text-fg-50">
                {isFirstMonth &&
                discountCredit > 0 &&
                rawUsageCost > usageCost ? (
                  <div className="flex items-center gap-1">
                    <span className="line-through text-fg-60 text-xs">
                      {formatCurrency(rawUsageCost)}
                    </span>
                    <span>{formatCurrency(discountedUsageCost)}</span>
                  </div>
                ) : (
                  formatCurrency(usageCost)
                )}
              </div>
            </div>
            {isFirstMonth && discountCredit > 0 && (
              <div className="flex items-center justify-between">
                <span className="text-xs text-fg-60">
                  Savings applied to your first month
                </span>
                <span className="text-sm font-semibold text-green-500">
                  -{formatCurrency(discountCredit)}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Bottom Row - 50/50 Split */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {/* Billing Cycle Container - Left 50% */}
          <div className="bg-bg-50 rounded-lg p-2.5 md:p-3 space-y-2 md:space-y-3">
            <h4 className="text-sm text-fg-50 font-medium">Billing Cycle</h4>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-fg-60">Period:</span>
                <span className="text-xs font-medium text-fg-50">Monthly</span>
              </div>
              {lastResetDate && (
                <div className="flex items-center justify-between">
                  <span className="text-xs text-fg-60">Started:</span>
                  <span className="text-xs font-medium text-fg-50">
                    {lastResetDate}
                  </span>
                </div>
              )}
              {nextResetDate && (
                <div className="flex items-center justify-between">
                  <span className="text-xs text-fg-60">Next Reset:</span>
                  <span className="text-xs font-medium text-fg-50">
                    {nextResetDate}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Value Metrics Container - Right 50% */}
          <div className="bg-bg-50 rounded-lg p-2.5 md:p-3 space-y-2 md:space-y-3">
            <h4 className="text-sm text-fg-50 font-medium">Value Metrics</h4>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-fg-60">Value Ratio:</span>
                <span className="text-xs font-medium text-fg-50">
                  {valueRatio}x
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-fg-60">Market Value:</span>
                <span className="text-xs font-medium text-fg-50">
                  {formatCurrency(marketValue)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-fg-60">You're Saving:</span>
                <span className="text-sm font-semibold text-green-500">
                  {formatCurrency(Math.max(savings, 0))}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading && !usageData) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-bg-50 rounded-full">
        <div className="animate-spin w-4 h-4 border-2 border-fg-60 border-t-transparent rounded-full"></div>
        <span className="text-sm text-fg-60">Loading...</span>
      </div>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="flex items-center gap-2 px-3 py-2 text-sm font-medium bg-bg-50 text-fg-60 hover:text-fg-80 hover:bg-bg-40 rounded-full transition-all duration-200 group"
        title="View Usage Statistics"
      >
        <div className="relative">
          <UsageMeterIcon width={16} height={16} className="text-fg-60" />
        </div>
        <span>Usage</span>
        <div className="flex items-center gap-1">
          <span
            className={`text-xs font-medium ${
              typeof usagePercentage === "number"
                ? getUsageColor(usagePercentage)
                : "text-fg-60"
            }`}
          >
            {typeof usagePercentage === "number"
              ? `${usagePercentage.toFixed(2)}%`
              : "..."}
          </span>
          <ChevronDown
            size={12}
            className={`text-fg-60 opacity-70 transition-transform duration-200 ${
              showDropdown ? "rotate-180" : ""
            }`}
          />
        </div>
      </button>

      {/* Mobile backdrop */}
      {showDropdown && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setShowDropdown(false)}
        />
      )}
      {showDropdown && (
        <div className="fixed md:absolute top-auto md:top-full bottom-0 md:bottom-auto left-0 right-0 md:left-0 md:right-auto md:mt-2 bg-white dark:bg-bg-60 rounded-t-2xl md:rounded-2xl shadow-xl border border-bd-50 z-50 overflow-hidden animate-slide-up md:animate-none md:w-96 md:max-w-md md:min-w-[320px] max-h-[85vh]">
          {/* Mobile header with close button */}
          <div className="flex items-center justify-between p-4 pb-2 md:hidden border-b border-bd-50">
            <h3 className="text-sm font-medium text-fg-50">Usage Details</h3>
            <button
              type="button"
              onClick={() => setShowDropdown(false)}
              className="p-1.5 rounded-lg text-fg-60 hover:text-fg-50 hover:bg-bg-50 transition-colors touch-manipulation"
              aria-label="Close"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
          {/* Tab Headers */}
          <div className="flex p-[2px] rounded-full bg-bg-50 m-3 md:m-3">
            <button
              onClick={() => setActiveTab("usage")}
              className={`flex-1 flex items-center justify-center gap-1.5 md:gap-2 px-2 md:px-3 py-2 text-xs md:text-sm font-medium rounded-full transition-colors touch-manipulation ${
                activeTab === "usage"
                  ? "bg-ac-02 text-white dark:text-black/70 shadow-sm "
                  : "text-fg-60 hover:text-fg-50 hover:bg-bg-40"
              }`}
            >
              <TrendingUp size={14} className="md:w-3.5 md:h-3.5" />
              Usage
            </button>
            <button
              onClick={() => setActiveTab("billing")}
              className={`flex-1 flex items-center justify-center gap-1.5 md:gap-2 px-2 md:px-3 py-2 text-xs md:text-sm font-medium rounded-full transition-colors touch-manipulation ${
                activeTab === "billing"
                  ? "bg-ac-02 text-white dark:text-black/70 shadow-sm "
                  : "text-fg-60 hover:text-fg-50 hover:bg-bg-40"
              }`}
            >
              <Receipt size={14} className="md:w-3.5 md:h-3.5" />
              Billing
            </button>
          </div>

          {/* Tab Content */}
          <div className="max-h-[60vh] md:max-h-[500px] overflow-y-auto custom-scrollbar">
            {activeTab === "usage" ? renderUsageTab() : renderBillingTab()}
          </div>
        </div>
      )}
    </div>
  );
};

export default Usage;
